import { IAngularEvent, IComponentOptions, IController, IOnChangesObject, IPromise, IQService, IRootScopeService } from "angular";
import { IAssigneesStoreState } from "@gtmhub/assignees";
import { ReactionService } from "@gtmhub/comments/services/reaction.service";
import { GtmhubController } from "@gtmhub/core";
import { IIndicator, IUIError, UIErrorHandlingService } from "@gtmhub/error-handling";
import { localize } from "@gtmhub/localization";
import { IExtendedRootScopeService } from "@gtmhub/shared/ng-extensions";
import { Ng1ConfirmDeleteService } from "@gtmhub/shared/services/ng1-confirm-delete.service";
import { SizeOfGif } from "@gtmhub/shared/services/tenor-gif.service";
import { USER_ROLES_VIEWONLY } from "@gtmhub/shared/utils/user-roles";
import { INgRedux } from "@gtmhub/state-management";
import { IReduxTeam, ITeamsStoreState, TeamsActions } from "@gtmhub/teams";
import { CurrentUserService, getCurrentUserId } from "@gtmhub/users";
import { IdMap, fields, flatten, unique } from "@gtmhub/util";
import { Assignee } from "@webapp/assignees/models/assignee.models";
import { createErrorObject } from "@webapp/error-handling/error-util";
import { RichTextEditorComponent } from "@webapp/shared/rich-text-editor/rich-text-editor.component";
import { isPreventBlurClass } from "@webapp/shared/rich-text-editor/rich-text-editor.utils";
import { CommentTargetType, IComment, IGif } from "../models/comments";
import { IAggregatedReaction, IReaction } from "../models/reactions";
import { CommentService } from "../services/comment.service";

const bindStateToScope = (state: IAssigneesStoreState & ITeamsStoreState): ICommentsRedux => ({
  assignees: state.assignees.map,
  teams: state.teams.map,
  reduxItemsFetched: state.assignees.isFetched && state.teams.isFetched,
  reduxItemsError: state.assignees.error || state.teams.error,
});

interface ICommentsRedux {
  assignees: Record<string, Assignee>;
  teams: IdMap<IReduxTeam>;
  reduxItemsFetched: boolean;
  reduxItemsError: IUIError;
}

interface ICommentsBindings {
  targetId: string;
  targetType: CommentTargetType;
  targetTitle: string;
  metadata: { [key: string]: string };
  initialMentions?: string[];
  focusOnLoad?: boolean;
  reactions?: boolean;
  disableCommentsView?: boolean;
  placeholder?: string;
  cancel(): void;
}

export interface CommentsCtrl extends ICommentsBindings, ICommentsRedux {}
export class CommentsCtrl extends GtmhubController implements IController {
  allMentionedShown: boolean;
  removeAllMentionsAriaLabel: string;
  comments: IComment[];
  currentUserId: string;
  editorButtonsDisabled: boolean;
  indicators: {
    loading?: IIndicator;
  };
  mentions: {
    removable?: string[];
    nonRemovable?: string[];
  } = {
    removable: [],
    nonRemovable: [],
  };
  editedCommentId: string;

  get commentEditedLabel(): string {
    return `(${localize("edited")})`;
  }

  get isCurrentUserAdmin(): boolean {
    return this.currentUserService.userHasRole("admin");
  }

  get isCurrentUserViewOnly(): boolean {
    return this.currentUserService.userHasRole(USER_ROLES_VIEWONLY);
  }

  private loaded: boolean;

  readonly sizeOfGif = SizeOfGif;

  static $inject = [
    "$rootScope",
    "$q",
    "$scope",
    "CommentService",
    "ReactionService",
    "$ngRedux",
    "TeamsActions",
    "UIErrorHandlingService",
    "CurrentUserService",
    "Ng1ConfirmDeleteService",
  ];

  constructor(
    private $rootScope: IRootScopeService,
    private $q: IQService,
    private $scope: IExtendedRootScopeService,
    private commentService: CommentService,
    private reactionService: ReactionService,
    private $ngRedux: INgRedux,
    private teamsActions: TeamsActions,
    private uiErrorHandlingService: UIErrorHandlingService,
    private currentUserService: CurrentUserService,
    private confirmDeleteService: Ng1ConfirmDeleteService
  ) {
    super();
    this.onDestroy($ngRedux.connect(bindStateToScope)(this));
  }

  $onInit(): void {
    this.$ngRedux.dispatch(this.teamsActions.fetchTeamsIfMissing());

    this.indicators = {
      loading: { progress: true },
    };
    this.currentUserId = getCurrentUserId();

    this.$q
      .all({
        comments: this.loadComments(),
        _: this.$scope.watchUntilAndRejectOnError(
          () => this.reduxItemsFetched,
          () => this.reduxItemsError
        ),
      })
      .then(
        () => {
          this.loaded = true;
          delete this.indicators.loading;

          this.initRemovableMentions();

          if (this.reactions) {
            this.$scope.$on("reactionCreated", (evt: IAngularEvent, reaction: IReaction) => this.onReactionCreated(reaction));
            this.$scope.$on("reactionDeleted", (evt: IAngularEvent, reaction: IAggregatedReaction) => this.onReactionDeleted(reaction));
          }

          this.$scope.$on("commentEdited", (event: IAngularEvent, comment: IComment) => this.onCommentEdited(comment));
          this.$scope.$on("commentDeleted", (event: IAngularEvent, comment: IComment) => this.onCommentDeleted(comment));

          this.$scope.$on("commentPostedExternally", (evt: IAngularEvent, targetId: string, targetType: CommentTargetType) =>
            this.onCommentPostedExternally(targetId, targetType)
          );
          this.$scope.$on("commentPostedFromWorkflowUpdate", (evt: IAngularEvent, input: { targetId: string; targetType: CommentTargetType }) => {
            this.onCommentPostedExternally(input.targetId, input.targetType);
          });
        },
        (error) => (this.indicators.loading = { error })
      );

    this.removeAllMentionsAriaLabel = `${localize("remove_all")} ${localize("mentions")}`;
  }

  $onChanges(onChangesObj: IOnChangesObject): void {
    const { initialMentions } = onChangesObj;
    if (initialMentions && !initialMentions.isFirstChange() && this.loaded) {
      this.initRemovableMentions();
    }
  }

  openInlineEditor(commentId: string): void {
    this.initRemovableMentions();
    this.editedCommentId = commentId;
    this.$scope.$evalAsync();
  }

  closeInlineEditor(): void {
    // (GVS-27436) blur caused by focusing search-gif field should not lead to closing the editor
    const activeElement = document.activeElement as HTMLElement;

    if (isPreventBlurClass(activeElement)) {
      return;
    }

    this.editedCommentId = null;
    this.reloadComments();
    this.$scope.$evalAsync();
  }

  openCommentDeleteConfirmation(comment: IComment): void {
    this.confirmDeleteService.confirmDeleteItem({ itemKey: "comment", itemToCase: true }).subscribe(() => {
      this.commentService
        .delete(comment.id, this.metadata)
        .then(() => {
          this.$rootScope.$broadcast("commentDeleted", comment);
          this.closeInlineEditor();
        })
        .catch((error) => this.uiErrorHandlingService.handleModal(error));
    });
  }

  isCurrentUserCommentAuthor(commentAuthorId: string): boolean {
    return commentAuthorId === this.currentUserId;
  }

  onCommentPosted(comment: IComment): void {
    this.$rootScope.$broadcast("commentPosted", comment);

    this.reloadComments();
  }

  createComment(args: { text: string; gif: IGif; mentions: string[]; editor: RichTextEditorComponent }): void {
    this.editorButtonsDisabled = true;
    this.$scope.$evalAsync();

    this.commentService
      .post(
        {
          targetId: this.targetId,
          targetType: this.targetType,
          targetTitle: this.targetTitle,
          text: args.text,
          mentioned: args.mentions,
          gif: args.gif,
        } as IComment,
        this.metadata
      )
      .then((res) => {
        this.onCommentPosted(res);
        args.editor.clearContent();
      })
      .catch((error) => {
        this.uiErrorHandlingService.handleModal(createErrorObject(error));
      })
      .finally(() => {
        this.editorButtonsDisabled = false;
        this.$scope.$evalAsync();
      });
  }

  editComment(args: { text: string; gif: IGif; mentions: string[] }): void {
    const editedComment = this.editedCommentId && this.comments.find((comment) => comment.id === this.editedCommentId);
    if (!editedComment) {
      return;
    }

    this.editorButtonsDisabled = true;
    this.$scope.$evalAsync();

    this.commentService
      .edit(
        this.editedCommentId,
        {
          text: args.text,
          mentioned: [...new Set(args.mentions.concat(editedComment.mentioned))],
          gif: args.gif ?? { id: "", searchQuery: "" },
        },
        {
          isDeleted: Boolean(editedComment.gif?.id && !args.gif?.id),
          isAdded: Boolean(!editedComment.gif?.id && args.gif?.id),
        },
        this.metadata
      )
      .then((res) => {
        this.onCommentEdited(res);
      })
      .catch((error) => {
        this.uiErrorHandlingService.handleModal(createErrorObject(error));
      })
      .finally(() => {
        this.editorButtonsDisabled = false;
        this.$scope.$evalAsync();
      });
  }

  onCommentEdited(comment: IComment): void {
    this.editedCommentId = null;
    const oldComment = this.comments.find((listComment) => listComment.id === comment.id);
    if (oldComment) {
      Object.assign(oldComment, comment);
      this.initRemovableMentions();
    }

    this.$scope.$evalAsync();
  }

  onCommentMentionsChanged(mentions: string[]): void {
    this.mentions.nonRemovable = mentions;

    // removes duplicated mention IDs from the removable mentions array
    const nonRemovable = new Set(this.mentions.nonRemovable);
    this.mentions.removable = (this.mentions.removable || []).filter((mentionId) => !nonRemovable.has(mentionId));

    this.$scope.$evalAsync();
  }

  removeMention(id: string): void {
    this.mentions.removable = this.mentions.removable.filter((mention) => mention !== id);
    this.$scope.$evalAsync();
  }

  removeAllMentions(): void {
    this.mentions.removable = [];
    this.$scope.$evalAsync();
  }

  showAllMentioned(): void {
    this.allMentionedShown = true;
  }

  private onCommentDeleted(comment: IComment): void {
    this.comments = this.comments.filter((listComment) => listComment.id !== comment.id);
    this.initRemovableMentions();
  }

  private loadComments(): IPromise<void> {
    if (this.disableCommentsView) {
      return null;
    }

    const commentFields = this.reactions
      ? fields(
          "id",
          "text",
          "mentioned",
          "targetType",
          "targetId",
          "createdAt",
          "modifiedAt",
          "createdBy",
          "modifiedBy",
          "seen",
          "targetTitle",
          "reactions",
          "gif",
          "editedBy"
        )
      : null;

    return this.commentService.getComments({ targetType: this.targetType, targetIds: this.targetId, fields: commentFields, sort: "-createdAt" }).then((comments) => {
      this.comments = comments.items;
      this.markAllAsSeen();
    });
  }

  private reloadComments() {
    this.loadComments().then(() => {
      this.initRemovableMentions();
    });
  }

  private initRemovableMentions(): void {
    const initialMentionedUsers = flatten(
      (Array.isArray(this.initialMentions) ? this.initialMentions : [this.initialMentions])
        .filter((id) => this.assignees[id]?.isActive)
        .map((id) => {
          if (this.assignees[id].type === "team") {
            const team = this.teams[id];
            if (team) {
              return [...(team.members || []), ...(team.manager ? [team.manager] : [])];
            }
          } else {
            return [id];
          }
        })
    );

    // can only be users
    const commentMentionedUsers = flatten((this.comments || []).map((c) => [...c.mentioned, c.createdBy]));

    this.mentions.removable = unique([...initialMentionedUsers, ...commentMentionedUsers]).filter((m) => m && m !== this.currentUserId);
  }

  private markAllAsSeen(): void {
    this.comments.forEach((comment) => {
      const isMentioned = comment.mentioned.some((mentionId) => this.currentUserId === mentionId);
      const hasSeen = comment.seen && comment.seen.some((seenUserId) => this.currentUserId === seenUserId);

      if (isMentioned && !hasSeen) {
        this.commentService.markAsSeen(comment.id);
      }
    });
  }

  public onReactionCreated(reaction: IReaction): void {
    const comment = (this.comments || []).find((c) => c.id === reaction.targetId);
    if (comment) {
      comment.reactions = this.reactionService.onReactionCreated(comment.reactions, reaction);
    }

    this.$scope.$evalAsync();
  }

  public onReactionDeleted(reaction: IAggregatedReaction): void {
    const comment = this.comments.find((c) => c.id === reaction.targetId);
    if (comment) {
      comment.reactions = this.reactionService.onReactionDeleted(comment.reactions, reaction);
    }

    this.$scope.$evalAsync();
  }

  private onCommentPostedExternally(targetId: string, targetType: CommentTargetType): void {
    if (targetId === this.targetId && targetType === this.targetType) {
      this.reloadComments();
    }
  }
}

export const CommentsComponent: IComponentOptions = {
  bindings: {
    targetId: "<",
    targetType: "<",
    targetTitle: "<",
    initialMentions: "<",
    metadata: "<",
    focusOnLoad: "<",
    reactions: "<",
    disableCommentsView: "<",
    placeholder: "<",
    cancel: "&",
  },
  controller: CommentsCtrl,
  template: require("./comments.html"),
};
